import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, MessageCircle, Send, Clock, MapPin } from "lucide-react";

const SUPPORT_EMAIL = "contato@rifarmc.com.br";
const WHATSAPP_URL = "https://chat.whatsapp.com/RifaRMC";

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Mensagem enviada! Nossa equipe responde em até 24h.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar. Tente novamente.");
    } finally { setSending(false); }
  };

  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(form.subject || "Contato Rifa RMC")}&body=${encodeURIComponent(
    `Nome: ${form.name}\nE-mail: ${form.email}\n\n${form.message}`
  )}`;

  return (
    <div className="max-w-6xl mx-auto px-6 py-14">
      <div className="mb-10 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-amber-200/80 mb-2">Suporte</p>
        <h1 className="heading text-3xl sm:text-5xl font-extrabold text-slate-50">Fale <span className="gold-gradient-text">conosco</span></h1>
        <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
          Tem alguma dúvida sobre rifas, pagamentos ou sorteios? Envie sua mensagem que responderemos em até 24 horas.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 card-luxury rounded-3xl p-6 sm:p-8">
          <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Nome completo</Label>
                <Input
                  data-testid="contact-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Seu nome"
                  className="mt-1.5 bg-slate-950 border-white/10 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">E-mail</Label>
                <Input
                  data-testid="contact-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="voce@email.com"
                  className="mt-1.5 bg-slate-950 border-white/10 text-slate-100"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Assunto</Label>
              <Input
                data-testid="contact-subject"
                required
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Ex.: Dúvida sobre pagamento PIX"
                className="mt-1.5 bg-slate-950 border-white/10 text-slate-100"
              />
            </div>
            <div>
              <Label className="text-slate-300">Mensagem</Label>
              <Textarea
                data-testid="contact-message"
                required
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Descreva sua dúvida ou solicitação..."
                className="mt-1.5 bg-slate-950 border-white/10 text-slate-100"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Button data-testid="contact-submit" type="submit" disabled={sending} className="gold-btn rounded-full h-12 px-6">
                <Send className="w-4 h-4 mr-2" /> {sending ? "Enviando..." : "Enviar mensagem"}
              </Button>
              <a href={mailtoHref} className="inline-flex items-center gap-2 rounded-full h-12 px-5 border border-white/10 text-slate-100 hover:bg-white/5 transition-all text-sm">
                <Mail className="w-4 h-4" /> Abrir no meu e-mail
              </a>
            </div>
          </form>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="card-luxury rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-200">
                <Mail className="w-5 h-5" />
              </span>
              <h3 className="heading text-lg font-bold text-slate-50">E-mail</h3>
            </div>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-amber-200 hover:text-amber-100 text-sm break-all">
              {SUPPORT_EMAIL}
            </a>
            <p className="text-xs text-slate-500 mt-2">Resposta em até 24 horas úteis.</p>
          </div>

          <div className="card-luxury rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-300">
                <MessageCircle className="w-5 h-5" />
              </span>
              <h3 className="heading text-lg font-bold text-slate-50">WhatsApp</h3>
            </div>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:text-emerald-200 text-sm">
              Entrar no grupo oficial
            </a>
            <p className="text-xs text-slate-500 mt-2">Suporte rápido e novidades das próximas rifas.</p>
          </div>

          <div className="card-luxury rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-200">
                <Clock className="w-5 h-5" />
              </span>
              <h3 className="heading text-lg font-bold text-slate-50">Atendimento</h3>
            </div>
            <p className="text-sm text-slate-300">Segunda a sábado</p>
            <p className="text-sm text-slate-400">09h às 20h</p>
          </div>

          <div className="card-luxury rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-300">
                <MapPin className="w-5 h-5" />
              </span>
              <h3 className="heading text-lg font-bold text-slate-50">Região</h3>
            </div>
            <p className="text-sm text-slate-300">Região Metropolitana de Campinas</p>
            <p className="text-xs text-slate-500 mt-1">São Paulo · Brasil</p>
          </div>
        </div>
      </div>
    </div>
  );
}
